import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadLanguage, tr, useStoreLanguage } from '@/i18n';
import { LANGUAGES, LANGUAGE_LABELS, LANGUAGE_STORAGE_KEY, isLanguage, localizedPath, splitLocalePath, type StoreLanguage } from '@/i18n/core';

export default function LanguageSelector({ light = false }: { light?: boolean }) {
  const language = useStoreLanguage();
  const location = useLocation();
  const latestLocation = useRef(location);
  latestLocation.current = location;
  const navigate = useNavigate();
  const [pending, setPending] = useState<StoreLanguage | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    if (pending && pending === language && splitLocalePath(location.pathname).language === pending) setPending(null);
  }, [language, location.pathname, pending]);
  const change = async (next: string) => {
    if (!isLanguage(next) || pending || next === language) return;
    setPending(next); setError('');
    try {
      await loadLanguage(next);
      const here = latestLocation.current;
      try { localStorage.setItem(LANGUAGE_STORAGE_KEY, next); } catch { /* Locale URL remains available. */ }
      navigate(localizedPath(here.pathname, next) + here.search + here.hash);
    } catch {
      setPending(null);
      setError('No se pudo cargar el idioma. Vuelve a intentarlo.');
    }
  };
  return <div className="relative flex shrink-0 items-center gap-1">
    <i aria-hidden="true" className={`ri-global-line text-base ${light ? 'text-white' : 'text-solsazon-copper'}`} />
    <label className="sr-only" htmlFor="store-language">{tr('Idioma')}</label>
    <select id="store-language" aria-label={tr('Idioma')} value={language} onChange={event => void change(event.target.value)} disabled={Boolean(pending)}
      className={`w-[82px] rounded-md border border-current/20 bg-transparent py-1.5 pl-1 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:opacity-60 ${light ? 'text-white' : 'text-solsazon-dark'}`}>
      {LANGUAGES.map(code => <option key={code} value={code} lang={code} className="bg-white text-gray-900">{LANGUAGE_LABELS[code]}</option>)}
    </select>
    {pending && <span role="status" className="sr-only">{tr('Cargando idioma…')}</span>}
    {error && <span role="alert" className="absolute right-0 top-full z-50 mt-2 w-56 rounded-lg bg-white p-3 text-xs text-red-700 shadow-lg">{tr(error)}</span>}
  </div>;
}
